<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import { PALETTE_ITEMS } from '../../constants/csmlNodeTemplates';
import { CATEGORY_STYLES } from '../../constants/csmlNodeCategories';

const props = defineProps({
  // Screen position the popover opens at (the "+" that was clicked).
  x: { type: Number, default: 0 },
  y: { type: Number, default: 0 },
});

const emit = defineEmits(['pick', 'close']);

const { t } = useI18n();
const search = ref('');
const rootRef = ref(null);

const PICKER_WIDTH = 240;
const PICKER_HEIGHT = 320;
const EDGE_GAP = 8;

// Keep the popover on screen when the "+" sits near the right or bottom edge.
const position = computed(() => ({
  left: `${Math.max(
    EDGE_GAP,
    Math.min(props.x, window.innerWidth - PICKER_WIDTH - EDGE_GAP)
  )}px`,
  top: `${Math.max(
    EDGE_GAP,
    Math.min(props.y, window.innerHeight - PICKER_HEIGHT - EDGE_GAP)
  )}px`,
}));

const items = computed(() => {
  const term = search.value.trim().toLowerCase();
  return PALETTE_ITEMS.filter(
    item => !term || t(item.labelKey).toLowerCase().includes(term)
  );
});

const onOutsideDown = event => {
  if (rootRef.value && !rootRef.value.contains(event.target)) emit('close');
};
const onKeyDown = event => {
  if (event.key === 'Escape') {
    event.stopPropagation();
    emit('close');
  }
};

onMounted(() => {
  window.addEventListener('mousedown', onOutsideDown, true);
  window.addEventListener('keydown', onKeyDown, true);
});
onBeforeUnmount(() => {
  window.removeEventListener('mousedown', onOutsideDown, true);
  window.removeEventListener('keydown', onKeyDown, true);
});
</script>

<template>
  <div
    ref="rootRef"
    class="fixed z-50 flex flex-col gap-2 p-2 border shadow-lg w-60 max-h-80 rounded-xl border-n-weak bg-n-solid-1"
    :style="position"
  >
    <Input
      v-model="search"
      size="sm"
      autofocus
      :placeholder="t('AGENT_BOTS.VISUAL_BUILDER.PALETTE_SEARCH')"
    />
    <div class="flex flex-col gap-0.5 overflow-y-auto">
      <p v-if="!items.length" class="px-2 py-3 text-xs text-n-slate-11">
        {{ t('AGENT_BOTS.VISUAL_BUILDER.PALETTE_NO_RESULTS') }}
      </p>
      <button
        v-for="item in items"
        :key="item.type"
        type="button"
        class="flex items-center gap-2 px-2 py-1.5 text-start rounded-lg hover:bg-n-alpha-2"
        @click="emit('pick', item.type)"
      >
        <span
          class="flex items-center justify-center rounded-md size-6 shrink-0"
          :class="CATEGORY_STYLES[item.category].paletteIcon"
        >
          <Icon :icon="item.icon" class="size-3.5" />
        </span>
        <span class="text-xs font-medium truncate text-n-slate-12">
          {{ t(item.labelKey) }}
        </span>
      </button>
    </div>
  </div>
</template>
